import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { createCondo } from "endpoints/condo/index.action";
import { useTranslation } from "react-i18next";
import "./AddCondominiumModal.scss";

interface AddCondominiumModalProps {
  isOpen: boolean;
  toggle: () => void;
  onCondoCreated?: () => void;
}

const AddCondominiumModal: React.FC<AddCondominiumModalProps> = ({
  isOpen,
  toggle,
  onCondoCreated,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    condoNumber: "",
    name: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    numberOfUnits: "",
    status: "",
  });
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Create condo mutation
  const createCondoMutation = useMutation({
    mutationFn: createCondo,
    onSuccess: (data) => {
      if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
        toast.success(data?.message || t("condominiums.condominiumAdded"), {
          position: "top-right",
          autoClose: 3000,
        });
        resetForm();
        toggle();
        // Call the callback to refresh the list
        if (onCondoCreated) {
          onCondoCreated();
        }
      } else {
        toast.error(data?.message || data?.response || t("condominiums.failedToLoad"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    },
    onError: (error: any) => {
      console.error("Error creating condo:", error);
      toast.error(error?.message || t("condominiums.failedToLoad"), {
        position: "top-right",
        autoClose: 3000,
      });
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t("condominiums.maxFileSize"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPreview(null);
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (
      !formData.name ||
      !formData.streetAddress ||
      !formData.numberOfUnits
    ) {
      toast.error(t("condominiums.condominiumNameRequired"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Map form data to API format
    const condoData = {
      name: formData.name,
      condoNumber: formData.condoNumber || undefined,
      address: formData.streetAddress,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zipCode: formData.zipCode || undefined,
      totalUnits: formData.numberOfUnits ? parseInt(formData.numberOfUnits, 10) : undefined,
      description: undefined, // Add description field if needed in the future
    };

    // Remove undefined values
    Object.keys(condoData).forEach((key) => {
      if (condoData[key as keyof typeof condoData] === undefined) {
        delete condoData[key as keyof typeof condoData];
      }
    });

    // Profile photo will be uploaded via separate API when ready
    // For now, we just store it in state for future use
    if (profilePhoto) {
      console.log("Profile photo ready for upload:", profilePhoto.name);
      // TODO: Upload profile photo via separate API endpoint when available
    }

    // Call the API
    createCondoMutation.mutate(condoData);
  };

  const resetForm = () => {
    setFormData({
      condoNumber: "",
      name: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      numberOfUnits: "",
      status: "",
    });
    setProfilePhoto(null);
    setPreview(null);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      className="add-condominium-modal"
    >
      <ModalHeader toggle={handleClose} className="add-condominium-modal-header">
        {t("condominiums.addNewCondominium")}
      </ModalHeader>
      <ModalBody className="add-condominium-modal-body">
        <p className="add-condominium-subtitle">
          {t("condominiums.addCondominiumDescription")}
        </p>
        <Form onSubmit={handleSubmit} className="add-condominium-form">
          <Row className="add-condominium-row">
            <Col md={6}>
              <FormGroup className="add-condominium-form-group">
                <Label for="condoNumber" className="add-condominium-label">
                  {t("condominiums.condoNumberShort")} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="condoNumber"
                  name="condoNumber"
                  value={formData.condoNumber}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterCondoNumber")}
                  className="add-condominium-input"
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="add-condominium-form-group">
                <Label for="name" className="add-condominium-label">
                  {t("condominiums.condominiumName")} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterCondominiumName")}
                  className="add-condominium-input"
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="add-condominium-form-group">
            <Label for="streetAddress" className="add-condominium-label">
              {t("condominiums.streetAddress")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="streetAddress"
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleInputChange}
              placeholder={t("condominiums.enterStreetAddress")}
              className="add-condominium-input"
              required
            />
          </FormGroup>

          <Row className="add-condominium-row">
            <Col md={4}>
              <FormGroup className="add-condominium-form-group">
                <Label for="city" className="add-condominium-label">
                  {t("condominiums.city")}
                </Label>
                <Input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterCity")}
                  className="add-condominium-input"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="add-condominium-form-group">
                <Label for="state" className="add-condominium-label">
                  {t("condominiums.stateProvince")}
                </Label>
                <Input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterState")}
                  className="add-condominium-input"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="add-condominium-form-group">
                <Label for="zipCode" className="add-condominium-label">
                  {t("condominiums.zipPostalCode")}
                </Label>
                <Input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterZipCode")}
                  className="add-condominium-input"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="add-condominium-row">
            <Col md={6}>
              <FormGroup className="add-condominium-form-group">
                <Label for="numberOfUnits" className="add-condominium-label">
                  {t("condominiums.numberOfUnitsShort")} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  id="numberOfUnits"
                  name="numberOfUnits"
                  value={formData.numberOfUnits}
                  onChange={handleInputChange}
                  placeholder={t("condominiums.enterNumberOfUnits")}
                  className="add-condominium-input"
                  min="1"
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="add-condominium-form-group">
                <Label for="status" className="add-condominium-label">
                  {t("common.status")}
                </Label>
                <Input
                  type="select"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="add-condominium-input add-condominium-select"
                >
                  <option value="">{t("condominiums.selectStatus")}</option>
                  <option value="active">{t("common.active")}</option>
                  <option value="inactive">{t("common.inactive")}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="add-condominium-form-group">
            <Label className="add-condominium-label">
              {t("condominiums.addProfilePhoto")}
            </Label>
            <div className="add-condominium-file-upload">
              {preview ? (
                <div className="add-condominium-preview-wrapper">
                  <img
                    src={preview}
                    alt="Preview"
                    className="add-condominium-preview"
                  />
                  <Button
                    type="button"
                    color="danger"
                    size="sm"
                    onClick={handleRemovePhoto}
                    className="add-condominium-remove-photo"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                </div>
              ) : (
                <label className="add-condominium-file-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="d-none"
                  />
                  <div className="add-condominium-file-content">
                    <i className="ri-camera-add-line"></i>
                    <p className="mb-1">{t("condominiums.chooseFileToUpload")}</p>
                    <p className="small text-muted">{t("condominiums.maxFileSize")}</p>
                  </div>
                </label>
              )}
            </div>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter className="add-condominium-modal-footer">
        <Button
          color="secondary"
          onClick={handleClose}
          className="add-condominium-cancel-btn"
        >
          {t("common.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          className="add-condominium-submit-btn"
          disabled={createCondoMutation.isPending}
        >
          {createCondoMutation.isPending ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t("condominiums.creating")}
            </>
          ) : (
            <>
              {t("condominiums.addCondominium")}
              <i className="ri-add-line ms-2"></i>
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddCondominiumModal;

